import { useClientDetailsQuery, useClientsQuery } from '../../lib/Queries';
import Loading from '../../components/Loading';
import { useEffect, useState } from 'react';
import { formatDateTime } from '../../lib/DateTime.js';
import SidePanel from '../../components/SidePanel.jsx';
import FormAnswers from '../../components/forms/submissions/FormAnswers';
import ClientDetails from '../../components/clients/ClientDetails.jsx';
import TextInputWithInlineLabel from '../../components/TextInputWithInlineLabel.jsx';
import MagnifyingGlassIcon from '../../components/icons/MagnifyingGlassIcon.jsx';
import NewClient from '../../components/clients/NewClient.jsx';
import ClientSearchResults from '../../components/clients/ClientSearchResults.jsx';
import ClientListItem from '../../components/clients/ClientListItem.jsx';
import ImportClientsModal from '../../components/clients/ImportClientsModal.jsx';
import ImportEventsModal from '../../components/calendar/ImportEventsModal.jsx';
import PageHeading from '../../components/PageHeading';
import DotDotDotMenu from '../../components/DotDotDotMenu';
import {
    Cog6ToothIcon,
    UserGroupIcon,
    CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import ClientSettingsModal from '~/components/clients/ClientSettingsModal';
import FeatureIntro from '~/components/FeatureIntro.jsx';

const ClientsIndexPage = ({}) => {
    const [isShowingNewClient, setIsShowingNewClient] = useState(false);
    const [isImportingClients, setIsImportingClients] = useState(false);
    const [isImportingEvents, setIsImportingEvents] = useState(false);
    const [isShowingSettings, setIsShowingSettings] = useState(false);

    const [search, setSearch] = useState();
    const [selectedClient, setSelectedClient] = useState(null);
    const { isLoading, data, refetch } = useClientsQuery();

    useEffect(() => {
        const params = new URL(document.location.href);
        if (params.searchParams.get('client')) {
            setSelectedClient({ id: params.searchParams.get('client') });
        }
    }, []);

    let clients = data || [];
    if (search) {
        clients = clients.filter(c => {
            const key = `${c.first_name} ${c.last_name} ${c.email} ${c.phone}`;
            return key.toLowerCase().indexOf(search.toLowerCase()) > -1;
        });
    }

    const groupedClients = Object.groupBy(clients, client => {
        return client.last_name[0]?.toUpperCase() || 'No last name';
    });

    const clientsByLetter = [];
    for (let letter in groupedClients) {
        clientsByLetter.push({
            letter: letter,
            clients: groupedClients[letter],
        });
    }

    return (
        <div>
            <PageHeading
                heading="Clients"
                headerSibling={
                    <DotDotDotMenu
                        btnClassName="btn-gray-circle"
                        options={[
                            {
                                name: 'Client Settings',
                                icon: <Cog6ToothIcon />,
                                onClick: () => setIsShowingSettings(true),
                            },
                            {
                                name: 'Import Clients',
                                icon: <UserGroupIcon />,
                                onClick: () => setIsImportingClients(true),
                            },
                            {
                                name: 'Import Events',
                                icon: <CalendarDaysIcon />,
                                onClick: () => setIsImportingEvents(true),
                            },
                        ]}
                    />
                }
                rightContent={
                    <button
                    type="button"
                    className="btn-pill text-nowrap ml-4"
                    onClick={() => setIsShowingNewClient(true)}
                    >
                        Add Client
                    </button>
                }
            />
            <ImportClientsModal
                isOpen={isImportingClients}
                onClose={() => setIsImportingClients(false)}
                onSuccess={refetch}
            />
            <ImportEventsModal
                isOpen={isImportingEvents}
                onClose={() => setIsImportingEvents(false)}
                onSuccess={refetch}
            />
            <ClientSettingsModal
                isOpen={isShowingSettings}
                onClose={() => setIsShowingSettings(false)}
            />

            <FeatureIntro>
                Manage your clients and their information in one place.
            </FeatureIntro>

            <div className="mb-4 flex items-center justify-between max-w-2xl">
                <TextInputWithInlineLabel
                    prefix={
                        <MagnifyingGlassIcon className="w-4 h-4 text-primary-700" />
                    }
                    inputClassName="pl-9"
                    className="w-full md:w-[60%]"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search..."
                />
            </div>
            <div className="flex">
                <div className="max-w-2xl w-full">
                    {isLoading && <Loading className="py-3" />}
                    {clientsByLetter.map(item => (
                        <div key={item.letter}>
                            <div className="pl-3 bg-gray-100  mt-4 pt-2 pb-2 text-gray-550 text-sm sticky top-0">
                                {item.letter}
                            </div>
                            <div className="panel pl-6">
                                {item.clients.map((client, idx) => (
                                    <ClientListItem
                                        key={client.id}
                                        client={client}
                                        onClick={() =>
                                            setSelectedClient(client)
                                        }
                                        isFirst={idx === 0}
                                        isSelected={
                                            client.id === selectedClient?.id
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                    {clients.length === 0 && !isLoading && (
                        <div className="py-4 panel pl-6">
                            {!search && (
                                <div>You have no clients (right now!)</div>
                            )}
                            {!!search && (
                                <div>
                                    There are no clients matching{' '}
                                    <span className="italic">{search}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <ClientDetails
                    client={selectedClient}
                    onClose={() => setSelectedClient(null)}
                    refetch={refetch}
                />

                <NewClient
                    isOpen={isShowingNewClient}
                    onClose={() => setIsShowingNewClient(false)}
                    refetch={refetch}
                />
            </div>

            <ClientSearchResults
                className="mt-6"
                search={search}
                setSelectedClient={setSelectedClient}
            />
        </div>
    );
};

export default ClientsIndexPage;
